import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { NamesOfColors, VerticalMenuIcon } from 'front-package-ui-kit';
import { Avatar, Divider, Popover, PopoverOrigin, Stack } from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import cn from 'classnames';

import { PopoverButton } from 'components/PopoverButton';
import { PATH_AUTH, PATH_DASHBOARD } from 'routes/paths';
import { AuthUser } from 'types/auth';
import { Typography } from 'components/Typography/Typography';
import { useCollapseDrawer } from 'hooks/useCollapseDrawer';
import { Profile } from 'assets/navbar/Profile';
import { useAuth } from '../../../hooks/useAuth';
import { Exit } from 'assets/navbar/Exit';

import s from './NavbarAccount.module.scss';

interface UserInfoProps {
  user: AuthUser;
  bigSize?: boolean;
}

const UserInfo = ({ user, bigSize = false }: UserInfoProps) => (
  <>
    <Avatar
      className={cn(s.avatar, { [s.bigAvatar]: bigSize })}
      src={undefined}
      alt="Avatar"
      variant="circular"
    >
      <PersonIcon sx={{ color: 'white', width: '24px', height: '24px' }} />
    </Avatar>

    <div className={s.info}>
      <Typography
        fontSize={bigSize ? '1rem' : '14px'}
        lineHeight={bigSize ? '24px' : '20px'}
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        width={bigSize ? '200px' : 'auto'}
      >
        {user?.name}
      </Typography>
      <Typography
        fontSize={bigSize ? '0.875rem' : '12px'}
        lineHeight={bigSize ? '20px' : '16px'}
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        color={'#00000099'}
        width={bigSize ? '200px' : 'auto'}
      >
        {user?.email}
      </Typography>
    </div>
  </>
);

export const NavbarAccount = () => {
  const navigate = useNavigate();

  const { user, logout } = useAuth();
  const { opened, hovered, tabs, setPath } = useCollapseDrawer();
  const [popover, setPopover] = useState<HTMLButtonElement | null>(null);

  const origin = {
    vertical: 'top',
    horizontal: 'right',
  } as PopoverOrigin;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setPopover(event.currentTarget);
  };

  const handleClose = () => {
    setPopover(null);
  };

  const handleProfile = () => {
    handleClose();

    setPath({ tab: null, item: null, subitem: null });

    navigate(PATH_DASHBOARD.index);
  };

  const handleLogout = async () => {
    try {
      handleClose();

      await logout();

      navigate(PATH_AUTH.login, { replace: true });
    } catch (error) {
      console.error(error);

      const enqueueSnackbar = (arg0: string, arg1: { variant: string }) => {
        throw new Error('Function not implemented.');
      };

      enqueueSnackbar('Ошибка выхода из панели управления!', { variant: 'error' });
    }
  };

  return (
    <>
      <div className={cn(s.account, { [s.closed]: !opened, [s.hovered]: !opened && hovered })}>
        <UserInfo user={user} />

        <button
          className={s.menu}
          aria-describedby={Boolean(popover) ? 'popover-user' : undefined}
          onClick={handleClick}
        >
          <VerticalMenuIcon size={16} color={NamesOfColors.grey60} />
        </button>
      </div>
      <Popover
        id={Boolean(popover) ? 'popover-user' : undefined}
        anchorEl={popover}
        open={Boolean(popover)}
        anchorOrigin={origin}
        onClose={handleClose}
      >
        <Stack sx={{ minWidth: '300px' }}>
          <Stack
            sx={{ flexDirection: 'row', gap: '16px', alignItems: 'center', padding: '14px 16px' }}
          >
            <UserInfo user={user} bigSize />
          </Stack>
          <Divider sx={{ margin: '8.5px 0' }} />
          <Stack sx={{ padding: '8px 0' }}>
            <PopoverButton onClick={handleProfile}>
              <Profile />
              <Typography marginLeft="4px" fontSize="1rem" lineHeight="24px" sx={{ opacity: 0.87 }}>
                Профиль
              </Typography>
            </PopoverButton>
            <PopoverButton onClick={handleLogout}>
              <Exit />
              <Typography fontSize="1rem" lineHeight="24px" sx={{ opacity: 0.87 }}>
                Выйти
              </Typography>
            </PopoverButton>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
};
